import type { OTCFloatingRateLoanFieldsData } from '@src/types/OTC';
import { isFloatingRateLoanProductFields } from '@src/types/OTC/otcGuards';
import { getFieldTitle, getFieldTooltip } from '@src/utils/fieldDefinitionUtils';
import { useRateSourceFixingsForAsset } from '@src/utils/fixtures/OTC/fixingFeedUtils';
import { fixedRateLoanFieldDefinitions } from '@src/utils/OTC/fixedRateLoanFieldDefinitions';
import { floatingRateLoanFieldDefinitions } from '@src/utils/OTC/floatingRateLoanFieldDefinitions';
import {
  appendTooltipWhenFeatureDisabled,
  OPEN_TERM_SUPPORT_COMING_SOON,
} from '@src/utils/OTC/otcFeatureFlagTooltipAppend';
import {
  ButtonVariants,
  DurationInput,
  FormGroup,
  HStack,
  LoanQuoteTerminationTypeEnum,
  NumberInput,
  SearchSelect,
  Stack,
  TextDividerSimple,
  ToggleGroup,
  type Leaves,
  type ToggleGroupOption,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { getLoanPaymentCurrencyOptionsForRow } from './loanPaymentCurrencyForRow';
import { isBulletLoanPaymentType } from './loanPaymentType';
import { LoanPeriodFields } from './LoanPeriodFields';
import {
  getDayCountConventionLabel,
  getLoanPaymentCurrencyLabel,
  getPrepaymentOptionLabel,
  getRateSourceLabel,
  getTermTypeLabel,
  LOAN_RESET_PERIOD_OPTIONS,
  LOAN_TERM_TYPE_OPTIONS,
  PREPAYMENT_OPTION_OPTIONS,
  useDayCountConventionOptions,
  type DayCountConventionOption,
  type LoanPaymentCurrencyOption,
  type LoanTermTypeOption,
  type PrepaymentOptionOption,
  type RateSourceOption,
} from './productLayoutTokens';
import type { OTCFieldsContext } from './types';
import { useLoanOtcTermTypeChange } from './useLoanOtcTermTypeChange';

const CALL_OPTION_OPTIONS: ToggleGroupOption<'true' | 'false'>[] = [
  { displayName: 'True', value: 'true' },
  { displayName: 'False', value: 'false' },
];

interface FloatingRateLoanProductLayoutProps {
  formData: OTCFloatingRateLoanFieldsData;
  disclosureContext: OTCFieldsContext;
  handleFieldChange: (field: Leaves<OTCFloatingRateLoanFieldsData>, value: string | boolean | null | undefined) => void;
  errors?: Record<string, string>;
  touched?: Record<string, boolean>;
  requiredFields?: Record<string, boolean>;
  enableLoanOpenTermSupport: boolean;
}

export const FloatingRateLoanProductLayout = ({
  formData,
  disclosureContext,
  handleFieldChange,
  errors = {},
  touched = {},
  requiredFields = {},
  enableLoanOpenTermSupport,
}: FloatingRateLoanProductLayoutProps) => {
  const rowData = disclosureContext.rowData;
  const baseAsset = rowData.baseAsset;
  const collateralAsset = 'collateralAsset' in rowData ? rowData.collateralAsset : null;
  const dayCountConventionOptions = useDayCountConventionOptions();

  const loanPaymentCurrencyOptions = useMemo((): LoanPaymentCurrencyOption[] => {
    return getLoanPaymentCurrencyOptionsForRow(baseAsset, collateralAsset);
  }, [baseAsset, collateralAsset]);

  const loanPaymentCurrencySelection = useMemo(() => {
    return loanPaymentCurrencyOptions.find(option => option.value === formData.loanPaymentCcy);
  }, [loanPaymentCurrencyOptions, formData.loanPaymentCcy]);

  const handleLoanPaymentCurrencyChange = useCallback(
    (option: LoanPaymentCurrencyOption | undefined) => {
      handleFieldChange('loanPaymentCcy', option?.value);
    },
    [handleFieldChange]
  );

  const termTypeSelection = useMemo((): LoanTermTypeOption | undefined => {
    return LOAN_TERM_TYPE_OPTIONS.find(
      option => option.value === (formData.termType ?? LoanQuoteTerminationTypeEnum.Fixed)
    ) as LoanTermTypeOption | undefined;
  }, [formData.termType]);

  const handleTermTypeChange = useLoanOtcTermTypeChange(handleFieldChange);
  const isBullet = isBulletLoanPaymentType(formData.loanPaymentType);

  const dayCountConventionSelection = useMemo(() => {
    return dayCountConventionOptions.find(option => option.value === formData.dayCountConvention);
  }, [dayCountConventionOptions, formData.dayCountConvention]);

  const handleDayCountConventionChange = useCallback(
    (option: DayCountConventionOption | undefined) => {
      handleFieldChange('dayCountConvention', option?.value);
    },
    [handleFieldChange]
  );

  const fixings = useRateSourceFixingsForAsset(baseAsset ?? undefined);
  const rateSourceOptions = useMemo((): RateSourceOption[] => {
    return fixings.map(fixing => ({
      displayName: fixing.displayName,
      value: fixing.index,
      description: fixing.description,
    }));
  }, [fixings]);

  const rateSourceSelection = useMemo(() => {
    return rateSourceOptions.find(option => option.value === formData.rateSource);
  }, [rateSourceOptions, formData.rateSource]);

  const handleRateSourceChange = useCallback(
    (option: RateSourceOption | undefined) => {
      handleFieldChange('rateSource', option?.value);
    },
    [handleFieldChange]
  );

  const prepaymentOptionSelection = useMemo(() => {
    return PREPAYMENT_OPTION_OPTIONS.find(option => option.value === formData.prepaymentOption);
  }, [formData.prepaymentOption]);

  const handlePrepaymentOptionChange = useCallback(
    (option: PrepaymentOptionOption | undefined) => {
      handleFieldChange('prepaymentOption', option?.value);
    },
    [handleFieldChange]
  );

  const isTermTypeFixed =
    (formData.termType ?? LoanQuoteTerminationTypeEnum.Fixed) === LoanQuoteTerminationTypeEnum.Fixed;

  if (!isFloatingRateLoanProductFields(formData)) {
    return null;
  }

  return (
    <Stack gap="spacing12" w="100%">
      <FormGroup
        label={getFieldTitle(fixedRateLoanFieldDefinitions.unitLoanAmount)}
        tooltip={getFieldTooltip(fixedRateLoanFieldDefinitions.unitLoanAmount)}
        required={requiredFields.unitLoanAmount}
        error={touched.unitLoanAmount ? errors.unitLoanAmount : undefined}
        mb="spacing4"
      >
        <NumberInput
          value={formData.unitLoanAmount ?? ''}
          suffix={disclosureContext.rowData.baseAsset}
          onChange={value => handleFieldChange('unitLoanAmount', value)}
          data-testid="floating-rate-loan-unit-loan-amount-input"
        />
      </FormGroup>

      <FormGroup
        label={getFieldTitle(floatingRateLoanFieldDefinitions.loanPaymentCcy)}
        tooltip={getFieldTooltip(floatingRateLoanFieldDefinitions.loanPaymentCcy)}
        required={requiredFields.loanPaymentCcy}
        error={touched.loanPaymentCcy ? errors.loanPaymentCcy : undefined}
        mb="spacing4"
      >
        <SearchSelect
          options={loanPaymentCurrencyOptions}
          selection={loanPaymentCurrencySelection}
          onChange={handleLoanPaymentCurrencyChange}
          getLabel={getLoanPaymentCurrencyLabel}
          placeholder="Select Loan Payment Currency"
          showClear
          data-testid="floating-rate-loan-payment-currency-select"
        />
      </FormGroup>

      <FormGroup
        label={getFieldTitle(floatingRateLoanFieldDefinitions.termType)}
        tooltip={appendTooltipWhenFeatureDisabled(
          getFieldTooltip(floatingRateLoanFieldDefinitions.termType),
          enableLoanOpenTermSupport,
          OPEN_TERM_SUPPORT_COMING_SOON
        )}
        required={requiredFields.termType}
        error={touched.termType ? errors.termType : undefined}
        mb="spacing4"
      >
        <SearchSelect<LoanTermTypeOption>
          options={LOAN_TERM_TYPE_OPTIONS}
          selection={termTypeSelection}
          onChange={handleTermTypeChange}
          getLabel={getTermTypeLabel}
          placeholder="Select Term Type"
          data-testid="floating-rate-loan-term-type-select"
          disabled={!enableLoanOpenTermSupport || isBullet}
        />
      </FormGroup>

      {isTermTypeFixed ? (
        <FormGroup
          label={getFieldTitle(fixedRateLoanFieldDefinitions.term)}
          tooltip={getFieldTooltip(fixedRateLoanFieldDefinitions.term)}
          required={requiredFields.term}
          error={touched.term ? errors.term : undefined}
          mb="spacing4"
        >
          <DurationInput
            initialValue={formData.term ?? undefined}
            onChange={value => handleFieldChange('term', value)}
            placeholder="Enter custom term"
            dataTestId="floating-rate-loan-term-input"
          />
        </FormGroup>
      ) : (
        <FormGroup
          label={getFieldTitle(floatingRateLoanFieldDefinitions.initialFixedTerm)}
          tooltip={getFieldTooltip(floatingRateLoanFieldDefinitions.initialFixedTerm)}
          required={requiredFields.initialTerm}
          error={touched.initialTerm ? errors.initialTerm : undefined}
          mb="spacing4"
        >
          <DurationInput
            initialValue={formData.initialTerm ?? undefined}
            onChange={value => handleFieldChange('initialTerm', value)}
            placeholder="Enter initial term"
            dataTestId="floating-rate-loan-initial-term-input"
          />
        </FormGroup>
      )}

      <LoanPeriodFields
        formData={formData}
        handleFieldChange={handleFieldChange}
        errors={errors}
        touched={touched}
        requiredFields={requiredFields}
        isOpenTerm={!isTermTypeFixed}
        testIdPrefix="floating-rate-loan"
        paymentPeriodField={floatingRateLoanFieldDefinitions.paymentPeriod}
        accrualPeriodField={floatingRateLoanFieldDefinitions.accrualPeriod}
        loanPaymentTypeField={floatingRateLoanFieldDefinitions.loanPaymentType}
      />

      <FormGroup
        label={getFieldTitle(floatingRateLoanFieldDefinitions.dayCountConvention)}
        tooltip={getFieldTooltip(floatingRateLoanFieldDefinitions.dayCountConvention)}
        required={requiredFields.dayCountConvention}
        error={touched.dayCountConvention ? errors.dayCountConvention : undefined}
        mb="spacing4"
      >
        <SearchSelect<DayCountConventionOption>
          options={dayCountConventionOptions}
          selection={dayCountConventionSelection}
          onChange={handleDayCountConventionChange}
          getLabel={getDayCountConventionLabel}
          placeholder="Select Day Count Convention"
          showClear
          data-testid="floating-rate-loan-day-count-convention-select"
        />
      </FormGroup>

      <TextDividerSimple text="Rate Settings" />

      <HStack w="100%" alignItems="center" gap="spacing12">
        <FormGroup
          label={getFieldTitle(floatingRateLoanFieldDefinitions.rateSource)}
          tooltip={getFieldTooltip(floatingRateLoanFieldDefinitions.rateSource)}
          required={requiredFields.rateSource}
          error={touched.rateSource ? errors.rateSource : undefined}
          flex={1}
          mb="spacing4"
        >
          <SearchSelect<RateSourceOption>
            options={rateSourceOptions}
            selection={rateSourceSelection}
            onChange={handleRateSourceChange}
            getLabel={getRateSourceLabel}
            getDescription={option => option.description ?? ''}
            placeholder="Select Rate Source"
            showClear
            data-testid="floating-rate-loan-rate-source-select"
          />
        </FormGroup>

        <FormGroup
          label={getFieldTitle(floatingRateLoanFieldDefinitions.resetPeriod)}
          tooltip={getFieldTooltip(floatingRateLoanFieldDefinitions.resetPeriod)}
          required={requiredFields.resetPeriod}
          error={touched.resetPeriod ? errors.resetPeriod : undefined}
          flex={1}
          mb="spacing4"
        >
          <ToggleGroup
            options={LOAN_RESET_PERIOD_OPTIONS}
            value={formData.resetPeriod}
            onChange={value => handleFieldChange('resetPeriod', value)}
            orientation="horizontal"
            selectionAnimation="slide"
            defaultSelectedVariant={ButtonVariants.Priority}
            data-testid="floating-rate-loan-reset-period-toggle"
          />
        </FormGroup>
      </HStack>

      <TextDividerSimple text="Additional Details" />

      <HStack w="100%" alignItems="center" gap="spacing12">
        <FormGroup
          label={getFieldTitle(floatingRateLoanFieldDefinitions.prepaymentOption)}
          tooltip={getFieldTooltip(floatingRateLoanFieldDefinitions.prepaymentOption)}
          required={requiredFields.prepaymentOption}
          error={touched.prepaymentOption ? errors.prepaymentOption : undefined}
          flex={1}
          mb="spacing4"
        >
          <SearchSelect<PrepaymentOptionOption>
            options={PREPAYMENT_OPTION_OPTIONS}
            selection={prepaymentOptionSelection}
            onChange={handlePrepaymentOptionChange}
            getLabel={getPrepaymentOptionLabel}
            placeholder="Select Prepayment Option"
            data-testid="floating-rate-loan-prepayment-option-select"
          />
        </FormGroup>

        <FormGroup
          label={getFieldTitle(floatingRateLoanFieldDefinitions.callOption)}
          tooltip={getFieldTooltip(floatingRateLoanFieldDefinitions.callOption)}
          required={requiredFields.callOption}
          error={touched.callOption ? errors.callOption : undefined}
          flex={1}
          mb="spacing4"
        >
          <ToggleGroup<'true' | 'false'>
            options={CALL_OPTION_OPTIONS}
            value={formData.callOption === true ? 'true' : 'false'}
            onChange={value => handleFieldChange('callOption', value === 'true')}
            orientation="horizontal"
            selectionAnimation="slide"
            defaultSelectedVariant={ButtonVariants.Priority}
            data-testid="floating-rate-loan-call-option-toggle"
          />
        </FormGroup>
      </HStack>
    </Stack>
  );
};
